import React, { useEffect, useState, useCallback } from "react";
import { Send, StickyNote, UserCheck, Inbox, Plus, Trash2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const STATUSES = ["new", "open", "closed"];
const STATUS_CLS = { new: "bg-amber-100 text-amber-700", open: "bg-blue-100 text-blue-700", closed: "bg-stone-100 text-stone-500" };

export default function MessagesAdmin() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const [staff, setStaff] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [reply, setReply] = useState("");
  const [note, setNote] = useState("");
  const [tplOpen, setTplOpen] = useState(false);
  const [mgr, setMgr] = useState(false);
  const [newTpl, setNewTpl] = useState({ title: "", body: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/admin/messages").then((r) => {
    setList(r.data);
    setSel((s) => (s ? r.data.find((m) => m.id === s.id) || null : null));
  }).catch(() => {}), []);
  useEffect(() => {
    load();
    api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {});
    api.get("/admin/reply-templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, [load]);

  const open = (m) => { setSel(m); setReply(""); setNote(""); if (!m.read) api.patch(`/admin/messages/${m.id}`, { read: true }).then(load); };
  const patch = async (id, body) => { try { await api.patch(`/admin/messages/${id}`, body); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const insertTpl = (t) => {
    const body = (t.body || "").replace(/\{\{GuestName\}\}/g, sel?.name || "there");
    setReply(body); setTplOpen(false);
  };
  const sendReply = async () => {
    if (!reply.trim() || !sel) return;
    setBusy(true);
    try { await api.post(`/admin/messages/${sel.id}/reply`, { text: reply.trim(), author: user?.name || user?.email || "Staff" }); toast.success("Reply sent"); setReply(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const addNote = async () => {
    if (!note.trim() || !sel) return;
    try { await api.post(`/admin/messages/${sel.id}/note`, { text: note.trim(), author: user?.name || user?.email || "Staff" }); setNote(""); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addTemplate = async () => {
    if (!newTpl.title.trim() || !newTpl.body.trim()) return;
    try { const { data } = await api.post("/admin/reply-templates", newTpl); setTemplates((t) => [...t, data]); setNewTpl({ title: "", body: "" }); toast.success("Template saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delTemplate = async (id) => { await api.delete(`/admin/reply-templates/${id}`); setTemplates((t) => t.filter((x) => x.id !== id)); };
  const delMessage = async (id) => { if (!window.confirm("Delete this message?")) return; try { await api.delete(`/admin/messages/${id}`); setSel(null); load(); toast.success("Message deleted"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <div className="grid lg:grid-cols-[320px_1fr] gap-4" data-testid="unified-inbox">
      {/* List */}
      <div className="card-lux overflow-hidden max-h-[75vh] overflow-y-auto">
        <div className="p-3 border-b border-stone-100 flex items-center gap-2 text-sm font-semibold text-forest"><Inbox size={16} /> Inbox <span className="text-stone-400 font-normal">({list.length})</span></div>
        {list.map((m) => {
          const st = m.status || "new";
          return (
            <button key={m.id} onClick={() => open(m)} data-testid={`msg-item-${m.id}`}
              className={`w-full text-left p-3 border-b border-stone-100 hover:bg-sand/50 ${sel?.id === m.id ? "bg-sand" : ""}`}>
              <div className="flex items-center justify-between">
                <span className={`font-medium text-forest text-sm ${!m.read ? "font-bold" : ""}`}>{m.name}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS_CLS[st]}`}>{st}</span>
              </div>
              <div className="text-xs text-stone-400 truncate">{m.message}</div>
              <div className="text-[10px] text-stone-400 mt-1 flex items-center gap-2">
                <span>Website</span>
                {m.assigned_to && <span className="text-rose">· {m.assigned_to}</span>}
                <span className="ml-auto">{new Date(m.created_at).toLocaleDateString()}</span>
              </div>
            </button>
          );
        })}
        {list.length === 0 && <p className="text-sm text-stone-400 p-4">No messages yet.</p>}
      </div>

      {/* Detail */}
      {!sel ? (
        <div className="card-lux p-10 flex items-center justify-center text-stone-400 text-sm">Select a message to view the conversation.</div>
      ) : (
        <div className="card-lux p-5 space-y-4" data-testid="msg-detail">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <div className="font-serif text-xl text-forest">{sel.name}</div>
              <div className="text-xs text-stone-500">{sel.email}{sel.phone ? ` · ${sel.phone}` : ""} · <span className="uppercase tracking-wide">Website</span></div>
            </div>
            <div className="flex items-center gap-2">
              <select value={sel.status || "new"} onChange={(e) => patch(sel.id, { status: e.target.value })} data-testid="msg-status" className={`text-xs rounded-full px-3 py-1 font-medium border-0 ${STATUS_CLS[sel.status || "new"]}`}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <div className="flex items-center gap-1">
                <UserCheck size={14} className="text-stone-400" />
                <select value={sel.assigned_to || ""} onChange={(e) => patch(sel.id, { assigned_to: e.target.value })} data-testid="msg-assign" className="text-xs rounded-lg border border-stone-200 px-2 py-1">
                  <option value="">Unassigned</option>
                  {staff.map((s) => <option key={s.email} value={s.name || s.email}>{s.name || s.email}</option>)}
                </select>
              </div>
              <button onClick={() => delMessage(sel.id)} data-testid="msg-delete" title="Delete message" className="p-1.5 rounded-md bg-red-50 text-red-600"><Trash2 size={14} /></button>
            </div>
          </div>

          <div className="bg-sand/40 rounded-lg p-3 text-sm text-stone-700 whitespace-pre-wrap" data-testid="msg-original">{sel.message}</div>

          {(sel.replies || []).length > 0 && (
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-wide text-stone-400">Conversation</div>
              {(sel.replies || []).map((r) => (
                <div key={r.id} className="bg-rose/5 border border-rose/10 rounded-lg p-3" data-testid={`msg-reply-${r.id}`}>
                  <div className="text-[11px] text-stone-400 mb-1">{r.author || "Staff"} · {new Date(r.created_at).toLocaleString()}</div>
                  <div className="text-sm text-stone-700 whitespace-pre-wrap">{r.text}</div>
                </div>
              ))}
            </div>
          )}

          {/* Reply box */}
          <div className="border-t border-stone-100 pt-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-wide text-stone-400">Reply to guest</span>
              <div className="relative">
                <button onClick={() => setTplOpen((o) => !o)} data-testid="tpl-toggle" className="text-xs text-rose font-semibold flex items-center gap-1">Templates <ChevronDown size={13} /></button>
                {tplOpen && (
                  <div className="absolute right-0 z-20 mt-1 w-64 rounded-lg border border-stone-200 bg-white shadow-xl p-1">
                    {templates.map((t) => (
                      <button key={t.id} onClick={() => insertTpl(t)} data-testid={`tpl-${t.id}`} className="w-full text-left px-3 py-2 text-sm rounded hover:bg-sand">{t.title}</button>
                    ))}
                    <button onClick={() => { setMgr(true); setTplOpen(false); }} className="w-full text-left px-3 py-2 text-xs text-stone-500 rounded hover:bg-sand flex items-center gap-1"><Plus size={12} /> Manage templates</button>
                  </div>
                )}
              </div>
            </div>
            <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={4} placeholder="Type your reply… (emailed to the guest)" data-testid="msg-reply-input" className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm" />
            <button onClick={sendReply} disabled={busy || !reply.trim()} data-testid="msg-send" className="btn-primary mt-2 disabled:opacity-50"><Send size={14} /> Send reply</button>
          </div>

          {/* Internal notes */}
          <div className="border-t border-stone-100 pt-3">
            <div className="text-xs uppercase tracking-wide text-stone-400 mb-2 flex items-center gap-1"><StickyNote size={13} /> Internal notes (staff only)</div>
            <div className="space-y-2 mb-2">
              {(sel.internal_notes || []).map((n) => (
                <div key={n.id} className="bg-amber-50 border border-amber-100 rounded-lg p-2.5" data-testid={`msg-note-${n.id}`}>
                  <div className="text-[11px] text-amber-700/70 mb-0.5">{n.author || "Staff"} · {new Date(n.created_at).toLocaleString()}</div>
                  <div className="text-sm text-stone-700 whitespace-pre-wrap">{n.text}</div>
                </div>
              ))}
              {(sel.internal_notes || []).length === 0 && <p className="text-xs text-stone-400">No notes yet.</p>}
            </div>
            <div className="flex gap-2">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note…" data-testid="msg-note-input" className="flex-1 rounded-lg border border-stone-200 px-3 py-2 text-sm" />
              <button onClick={addNote} disabled={!note.trim()} data-testid="msg-note-add" className="btn-dark disabled:opacity-50"><Plus size={14} /> Note</button>
            </div>
          </div>
        </div>
      )}

      {/* Template manager modal */}
      {mgr && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setMgr(false)} data-testid="tpl-manager">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-xl text-forest mb-4">Quick-reply templates</h3>
            <p className="text-xs text-stone-500 mb-3">Use <code className="bg-sand px-1 rounded">{"{{GuestName}}"}</code> and <code className="bg-sand px-1 rounded">{"{{DoorCode}}"}</code> as placeholders.</p>
            <div className="space-y-2 mb-4">
              {templates.map((t) => (
                <div key={t.id} className="flex items-start justify-between bg-sand/50 rounded-lg p-3">
                  <div><div className="font-medium text-forest text-sm">{t.title}</div><div className="text-xs text-stone-500 whitespace-pre-wrap line-clamp-2">{t.body}</div></div>
                  <button onClick={() => delTemplate(t.id)} data-testid={`tpl-del-${t.id}`} className="text-red-500 hover:text-red-700 shrink-0 ml-2"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
            <div className="space-y-2 border-t border-stone-100 pt-3">
              <input value={newTpl.title} onChange={(e) => setNewTpl({ ...newTpl, title: e.target.value })} placeholder="Template title" data-testid="tpl-new-title" className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm" />
              <textarea value={newTpl.body} onChange={(e) => setNewTpl({ ...newTpl, body: e.target.value })} rows={4} placeholder="Template body…" data-testid="tpl-new-body" className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm" />
              <button onClick={addTemplate} data-testid="tpl-new-save" className="btn-primary"><Plus size={14} /> Add template</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
